import * as React from 'react';
import * as Moment from 'moment';
import Loading, { FontSize } from '../../misc/components/loading';
import { QueryTable, QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import { QueryTableState, QueryTableActions, ReceiveQueryAction, PageAction, SortAction, FilterAction, ClearFilterAction } from "../../misc/components/queryTable/Hooks/UseQueryTable";

import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Authorization } from '../../misc/components/Authorization';
import { Query, Subject, Permission, QueryFilter, StudySubjectSetting } from "../../misc/api/apiTypes";

import FilterModals from './SubjectsTableFilters';
import * as Shared from '../SubjectsShared';
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { isColumnHidden } from '../isColumnHidden';
import { Link, useHistory } from 'react-router-dom';
import { StartSessionWizard } from '../StartSessionWizard/StartSessionWizard';
import { useSecurityContext } from '../../Core/SecurityContext';
import { SubjectMutation, WebPortalClient } from 'webportalclient';
import { Subject as SubjectV3 } from 'webportalclient';
import { AuditModal } from '../../auditmodal/components/AuditModal';
import { ConfigurableDataItemIds } from '../../misc/api/ApiConfigurableDataItems';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { useIdleTimerContext } from '../../Core/IdleTimerContext';

declare const rootUrl: string;

interface SubjectsTableOwnProps {
    queryTableReducer: [QueryTableState, React.Dispatch<QueryTableActions>];
}


interface SubjectsTableProps extends SubjectsTableOwnProps {

}

export const SubjectsTable = ({
    queryTableReducer,
}: SubjectsTableProps): JSX.Element => {

    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedFilterModal, setSelectedFilterModal] = React.useState<string>(null);
    const [startSessionVisibleForSubjectId, setStartSessionVisibleForSubjectId] = React.useState<string>();
    const { dataItemConfigurationCollection, studySubjectSetting } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    const [terminated, setTerminated] = React.useState<boolean>(false);
    const [subjectToTerminate, setSubjectToTerminate] = React.useState<SubjectV3>();
    const [submittingSubjectTermination, setSubmittingSubjectTermination] = React.useState<boolean>(false);
    const [forceUpdateTable, setForceUpdateTable] = React.useState<boolean>(false);
    const client = useWebPortalApiClient();
    const [legacyClient] = React.useState(useWebPortalLegacyClient());

    const [queryState, tableDispatch] = queryTableReducer;
    const history = useHistory();
    const { timer } = useIdleTimerContext();

    React.useEffect(() => {
        setIsLoading(true);

        legacyClient.getSubjects(queryState.query).then((_populatedQuery) => {
            updateQueryData(_populatedQuery);
            setIsLoading(false);
        });

    }, [queryState.query.Page.Current, queryState.query.Order.Member, queryState.query.Order.Direction, queryState.query.Filters, forceUpdateTable]);

    const updateQueryData = (query: Query<Subject>) => tableDispatch(ReceiveQueryAction.create(query));

    const RenderMenu = (
        row: Subject,
        currentPermissions: Permission[],
    ) => {
        return (
            <div className="text-center">
                <div className="btn-group" role="group">
                    <button
                        type="button"
                        className="btn btn-primary btn-xs dropdown-toggle"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        <i className="fa fa-bars" aria-hidden="true" />
                    </button>
                    <ul className="dropdown-menu pull-right scrollable-menu" role="menu">
                        <Authorization
                            permissionIdsAny={[PermissionEnum.ViewSubjects, PermissionEnum.ViewSiteSubjects]}
                            studyId={row.Study.Id}
                            currentPermissions={currentPermissions}
                        >
                            <li>
                                <Link to={`/subjects/view/${row.Id}`}>View</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditSubject, PermissionEnum.EditSiteSubjects]}
                            studyId={row.Study.Id}
                            currentPermissions={currentPermissions}
                        >
                            <li>
                                <Link to={`/Subjects/Edit/${row.Id}`}>Edit</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionId={PermissionEnum.StartSession}
                            studyId={row.Study.Id}
                            currentPermissions={currentPermissions}
                        >
                            <li>
                                <a style={{ cursor: 'pointer' }} onClick={() => { timer.pause(); setStartSessionVisibleForSubjectId(row.Id) }}>Start Session...</a>
                            </li>
                        </Authorization>
                    </ul>
                </div>
            </div>
        );
    };

    const handleFilter = (member: string) => setSelectedFilterModal(member);
    const handlePage = (page: number) => tableDispatch(PageAction.create(page));
    const handleSort = (member: string) => tableDispatch(SortAction.create(member));

    const filterModalOnClose = (values: QueryFilter[]) => {
        values.forEach((item) => (
            tableDispatch(FilterAction.create(item.Member, item.Condition, item.Values))
        ));

        setSelectedFilterModal(null);
    }

    const filterModalOnClear = (member: string) => {
        tableDispatch(ClearFilterAction.create(member));
        setSelectedFilterModal(null);
    }

    const getColumnName = (itemId: ConfigurableDataItemIds) => {
        if (dataItemConfigurationCollection === null)
            return '';

        return dataItemConfigurationCollection.find(x => x.configurableDataItemId === itemId).displayText;
    };

    const getPopulationGroupValue = (row: Subject) => {
        if (row.SelfAssessedPopulationGroup) {
            return <>
                <span data-placement="top" data-toggle="tooltip" title={row.SelfAssessedPopulationGroup}>
                    <span>
                        {row.PopulationGroupName}
                        &nbsp;
                        <i aria-hidden="true" className="fa fa-users text-muted"></i>
                    </span>
                </span>
            </>;
        } else {
            return <>
                {row.PopulationGroupName ? row.PopulationGroupName : "-"}
            </>;
        }
    }

    if (dataItemConfigurationCollection.length === 0 || isLoading) return <Loading />;

    return (
        <>
            <QueryTable id="subjectsTable" query={queryState.query} rowUniqueIdentifier={(row) => row.Id} onPage={handlePage} onSort={handleSort} onFilter={handleFilter} isLoading={isLoading}>
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectId, dataItemConfigurationCollection)}
                    member={Shared.SubjectNumberFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectId)}
                    map={(row) => row.SubjectNumber ? row.SubjectNumber : "-"}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.RandomizationNumber, dataItemConfigurationCollection)}
                    member={Shared.SubjectRandomizationNumberFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.RandomizationNumber)}
                    map={(row) => row.RandomisedId ? row.RandomisedId : "-"}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    isHidden={false}
                    member={Shared.SiteNumberFilterMember}
                    header="Site ID"
                    map={(row) => row.Site.SiteNumber}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectDateOfBirth, dataItemConfigurationCollection)}
                    member={Shared.SubjectDateOfBirthFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectDateOfBirth)}
                    map={(row) => row.BirthDate ? Moment.parseZone(row.BirthDate).format("DD-MMM-YYYY") : "-"}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectAge, dataItemConfigurationCollection)}
                    member={Shared.SubjectAgeFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectAge)}
                    map={(row) => row.Age ? row.Age : "-"}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectGender, dataItemConfigurationCollection)}
                    member={Shared.SubjectGenderFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectGender)}
                    map={(row) => row.Gender ? row.Gender.Name : "-"}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectHeight, dataItemConfigurationCollection)}
                    member={Shared.SubjectHeightFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectHeight)}
                    map={(row) => row.Height ? row.Height : "-"}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectWeight, dataItemConfigurationCollection)}
                    member={Shared.SubjectWeightFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectWeight)}
                    map={(row) => row.Weight ? row.Weight : "-"}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectPopulationGroup, dataItemConfigurationCollection)}
                    member={Shared.SubjectPopulationGroupFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectPopulationGroup)}
                    map={(row) => getPopulationGroupValue(row)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    isHidden={isColumnHidden(ConfigurableDataItemIds.SubjectSmokerType, dataItemConfigurationCollection)}
                    member={Shared.SubjectSmokingStatusFilterMember}
                    header={getColumnName(ConfigurableDataItemIds.SubjectSmokerType)}
                    map={(row) => row.SmokerType?.Name ?? '-'}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    isHidden={!studySubjectSetting?.canEarlyTerminateSubject}
                    member={Shared.SubjectTerminationDateFilterMember}
                    header="Termination Date"
                    map={(row) => row.TerminationDate ? <LocalizedDateTime time={row.TerminationDate} accuracy={DateTimeAccuracy.Minute} isTooltip={true} /> : "-"}
                    sortable={true}
                    filterable={false}
                />
                <QueryTableColumn
                    member=""
                    header=""
                    map={row => RenderMenu(
                        row,
                        currentUserPermissions
                    )
                    }
                />
            </QueryTable>
            {FilterModals(legacyClient, dataItemConfigurationCollection, queryState, selectedFilterModal, filterModalOnClose, filterModalOnClear)}
            {startSessionVisibleForSubjectId ? <StartSessionWizard
                subjectId={startSessionVisibleForSubjectId}
                onWizardClose={(redirect) => {
                    setStartSessionVisibleForSubjectId(undefined);
                    timer.resume();
                    if (redirect) {
                        history.push(redirect);
                    }  
                }} /> : null}
        </>
    );
}
